import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Switch, Button, IconButton,
  FormControlLabel, FormGroup, FormLabel, Select, MenuItem, InputLabel,
  Container, Paper, Grid, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Snackbar, CircularProgress, Stack, Radio, RadioGroup, Checkbox
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import useAxiosPrivate from "../utils/useAxiosPrivate";
import { useAuth } from "../utils/AuthProvider";
import { canViewInternalDetails } from "../utils/permissions";
import { usePhoneVerification } from "../utils/PhoneVerificationProvider";
import MuiAlert from '@mui/material/Alert';
import i18n from '../translationUtils/i18n';

export default function NotificationSettings() {

  const [notificationId, setNotificationId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [pressureOn, setPressureOn] = useState(false);
  const [sprayOn, setSprayOn] = useState(false);
  const [hotWorksAlerts, setHotWorks] = useState(false);
  const [testAlertsOn, setTestAlertsOn] = useState(false);
  const [waterOn, setWaterOn] = useState(false);
  const [eButtonOn, setEButtonOn] = useState(false);
  const [eCareOn, setECareOn] = useState(false);
  const [weekdayValue, setWeekdayValue] = useState('off');
  const [weekdaysOn, setWeekdaysOn] = useState(false);
  const [weekdayScheduleOn, setWeekdayScheduleOn] = useState(false);
  const [weekendValue, setWeekendValue] = useState('off');
  const [weekendsOn, setWeekendsOn] = useState(false);
  const [weekendScheduleOn, setWeekendScheduleOn] = useState(false);
  const [weekdaysStartTime, setWeekDaysStartTime] = useState(null);
  const [weekdaysEndTime, setWeekDaysEndTime] = useState(null);
  const [weekendsStartTime, setWeekEndsStartTime] = useState(null);
  const [weekendsEndTime, setWeekEndsEndTime] = useState(null);
  const [weekdayNotificationMode, setWeekdayNotificationMode] = useState("accumulation");
  const [weekendNotificationMode, setWeekendNotificationMode] = useState("accumulation");
  const [name, setName] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [email, setEmail] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("1");
  const [userTimeZone, setUserTimeZone] = useState(null);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [smsOn, setSMSOn] = useState(false);
  const [whatsAppOn, setWhatsAppOn] = useState(false);
  const [whatsAppConsent, setWhatsAppConsent] = useState(false);
  const [whatsAppModalVisible, setWhatsAppModalVisible] = useState(false);
  const [whatsAppConsentChecked, setWhatsAppConsentChecked] = useState(false);
  const [pushNotificationsOn, setPushNotificationsOn] = useState(false);
  const [emailsOn, setEmailsOn] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { refreshPhoneVerification } = usePhoneVerification();

  const widthStyle = { width: "fit-content" };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchData = () => {
    const controller = new AbortController();

    const getSettings = async () => {
      try {
        const response = await axiosPrivate.get('/api/User/settings/' + cookies.user, {
          signal: controller.signal
        });

        loadData(response.data);

      } catch (err) {
        console.log(err);
      }
    }

    getSettings();

    return () => {
      controller.abort();
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line 
  }, []);

  const loadData = (settings) => {
    setNotificationId(settings.notificationId);
    setUserId(settings.userId);
    setPressureOn(settings.pressureOn);
    setSprayOn(settings.sprayOn);
    setHotWorks(settings.hotWorksAlerts);
    setTestAlertsOn(settings.testAlertsOn);
    setWaterOn(settings.waterOn ? settings.waterOn : settings.pressureOn);
    setEButtonOn(settings.eButtonOn);
    setECareOn(settings.eCare);
    setSMSOn(settings.sms);
    setWhatsAppOn(settings.whatsApp);
    setWhatsAppConsent(settings.whatsAppConsent ?? false);
    setPushNotificationsOn(settings.pushNotification);
    setEmailsOn(settings.emailNotification);
    setUserTimeZone(settings.userTimeZone);
    setWeekdayNotificationMode(settings.weekdayNotificationMode ?? "accumulation");
    setWeekendNotificationMode(settings.weekendNotificationMode ?? "accumulation");
    setName(settings.fullName);
    setCellPhone(settings.countryCode + " " + settings.cellPhone);
    setEmail(settings.email);
    setPreferredLanguage(settings.prefferedLanguage);
    setWeekdayValue(handleLoadChange(settings.weekdays, settings.weekdaySchedule, settings.weekdayNotificationMode ?? "accumulation"));
    setWeekendValue(handleLoadChange(settings.weekends, settings.weekendSchedule, settings.weekendNotificationMode ?? "accumulation"));
    setWeekdaysOn(settings.weekdays);
    setWeekendsOn(settings.weekends);
    setWeekdayScheduleOn(settings.weekdaySchedule);
    setWeekendScheduleOn(settings.weekendSchedule);
    setWeekDaysStartTime(dayjs(`2023-01-01T${settings.weekdayStartTime}`));
    setWeekDaysEndTime(dayjs(`2023-01-01T${settings.weekdayEndTime}`));
    setWeekEndsStartTime(dayjs(`2023-01-01T${settings.weekendStartTime}`));
    setWeekEndsEndTime(dayjs(`2023-01-01T${settings.weekendEndTime}`));
  }

  const handleSave = () => {

    setLoading(true);

    const notifSettings = {
      notificationId: notificationId,
      userId: userId,
      pressureOn: pressureOn,
      sprayOn: sprayOn,
      hotWorksAlerts: hotWorksAlerts,
      testAlertsOn: testAlertsOn,
      waterOn: waterOn,
      eButton: eButtonOn,
      eCare: eCareOn,
      sms: smsOn,
      whatsApp: whatsAppOn,
      whatsAppConsent: whatsAppConsent,
      pushNotification: pushNotificationsOn,
      emailNotification: emailsOn,
      weekdayNotificationMode: weekdayNotificationMode,
      weekendNotificationMode: weekendNotificationMode,
      fullName: name,
      cellPhone: cellPhone.substring(cellPhone.indexOf(' ') + 1),
      email: email,
      countryCode: cellPhone.substring(0, cellPhone.indexOf(' ')),
      prefferedLanguage: preferredLanguage,
      userTimeZone: userTimeZone,
      weekdays: weekdaysOn,
      weekEnds: weekendsOn,
      weekdaySchedule: weekdayScheduleOn,
      weekEndSchedule: weekendScheduleOn,
      weekdayStartTime: weekdayScheduleOn && weekdaysStartTime && dayjs(weekdaysStartTime).isValid() ? new Date(weekdaysStartTime).toLocaleTimeString('en-US', { hour12: false }) : null,
      weekdayEndTime: weekdayScheduleOn && weekdaysEndTime && dayjs(weekdaysEndTime).isValid() ? new Date(weekdaysEndTime).toLocaleTimeString('en-US', { hour12: false }) : null,
      weekendStartTime: weekendScheduleOn && weekendsStartTime && dayjs(weekendsStartTime).isValid() ? new Date(weekendsStartTime).toLocaleTimeString('en-US', { hour12: false }) : null,
      weekendEndTime: weekendScheduleOn && weekendsEndTime && dayjs(weekendsEndTime).isValid() ? new Date(weekendsEndTime).toLocaleTimeString('en-US', { hour12: false }) : null
    };

    axiosPrivate.post("/api/NotificationSettings/saveNotificationSettings", notifSettings)
      .then(() => {
        fetchData();
        refreshPhoneVerification();
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error(error);
        alert("Error saving settings. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      })
  };

  const handleSms = (sms) => {
    if (sms && !smsOn) {
      alert(`${i18n.t('smsenabled')}: ${i18n.t('messageratesmayapply')}`);
    }
    setSMSOn(!smsOn);
  }

  const handleWhatsApp = () => {
    if (!whatsAppOn) {
      setWhatsAppConsentChecked(false);
      setWhatsAppModalVisible(true);
      return;
    }
    setWhatsAppOn(false);
    setWhatsAppConsent(false);
  }

  const handleWhatsAppConsentCancel = () => {
    setWhatsAppModalVisible(false);
    setWhatsAppConsentChecked(false);
  }

  const handleWhatsAppConsentConfirm = () => {
    setWhatsAppOn(true);
    setWhatsAppConsent(true);
    setWhatsAppModalVisible(false);
  }

  const handlePreAction = () => {
    setPressureOn(!pressureOn);
    setWaterOn(!waterOn);
  };

  const languages = [
    { label: i18n.t('english'), value: 'en' },
    { label: i18n.t('french'), value: 'fr' },
    { label: i18n.t('spanish'), value: 'es' }
  ];

  const handleWeekdayChange = (event) => {
    let selected = event.target.value;
    setWeekdayValue(selected);
    if (selected === 'on24/7') {
      setWeekdaysOn(true);
      setWeekdayScheduleOn(false);
    } else if (selected === 'off') {
      setWeekdaysOn(false);
      setWeekdayScheduleOn(false);
    } else if (selected === 'accumulation') {
      setWeekdaysOn(true);
      setWeekdayScheduleOn(true);
      setWeekdayNotificationMode('accumulation');
    } else if (selected === 'startStop') {
      setWeekdaysOn(true);
      setWeekdayScheduleOn(true);
      setWeekdayNotificationMode('startStop');
    }
  };

  const handleWeekendChange = (event) => {
    let selected = event.target.value;
    setWeekendValue(selected);
    if (selected === 'on24/7') {
      setWeekendsOn(true);
      setWeekendScheduleOn(false);
    } else if (selected === 'off') {
      setWeekendsOn(false);
      setWeekendScheduleOn(false);
    } else if (selected === 'accumulation') {
      setWeekendsOn(true);
      setWeekendScheduleOn(true);
      setWeekendNotificationMode('accumulation');
    } else if (selected === 'startStop') {
      setWeekendsOn(true);
      setWeekendScheduleOn(true);
      setWeekendNotificationMode('startStop');
    }
  };

  const handleLoadChange = (days, schedule, mode) => {
    if (days && !schedule) {
      return 'on24/7'
    } else if (days && schedule && mode === 'accumulation') {
      return 'accumulation'
    } else if (days && schedule && mode === 'startStop') {
      return 'startStop'
    } else {
      return 'off'
    }
  }

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container style={{ paddingTop: "40px" }}>
          <Typography variant="h4" style={{ paddingBottom: "20px" }}>{i18n.t('notificationsettings')}</Typography>
          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" paddingLeft={3} gutterBottom>{i18n.t('alertsiwanttoreceive')}</Typography>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={pressureOn && waterOn} onChange={handlePreAction} />}
                label={i18n.t('verifiedfire')}
                style={widthStyle}
              />
              <FormControlLabel
                control={<Switch checked={sprayOn} onChange={() => setSprayOn(!sprayOn)} />}
                label={i18n.t('suppressionactivated')}
                style={widthStyle}
              />
              {/* <FormControlLabel
                control={<Switch checked={hotWorksAlerts} onChange={() => setHotWorks(!hotWorksAlerts)} />}
                label={i18n.t('mobilesuppressionunit')}
                style={widthStyle}
              /> */}
              {canViewInternalDetails(cookies.role) &&
                <FormControlLabel
                  control={<Switch checked={testAlertsOn} onChange={() => setTestAlertsOn(!testAlertsOn)} />}
                  label={i18n.t('testalerts')}
                  style={widthStyle}
                />
              }
            </FormGroup>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" paddingLeft={3} gutterBottom>{i18n.t('alertdeliverymethods')}</Typography>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={smsOn} onChange={handleSms} />}
                label={i18n.t('textnotifications')}
                style={widthStyle}
              />
              <FormControlLabel
                control={<Switch checked={whatsAppOn} onChange={handleWhatsApp} />}
                label={i18n.t('whatsappnotifications')}
                style={widthStyle}
              />
              <Dialog open={whatsAppModalVisible} onClose={handleWhatsAppConsentCancel}>
                <DialogTitle>{i18n.t('whatsappconsenttitle')}</DialogTitle>
                <DialogContent>
                  <Typography>{i18n.t('whatsappconsenttip')}</Typography>
                  <FormControlLabel
                    sx={{ marginTop: 2 }}
                    control={
                      <Checkbox
                        checked={whatsAppConsentChecked}
                        onChange={(e) => setWhatsAppConsentChecked(e.target.checked)}
                      />
                    }
                    label={i18n.t('whatsappconsentcheckbox')}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleWhatsAppConsentCancel}>{i18n.t('cancel')}</Button>
                  <Button
                    variant="contained"
                    disabled={!whatsAppConsentChecked}
                    onClick={handleWhatsAppConsentConfirm}
                  >
                    {i18n.t('confirm')}
                  </Button>
                </DialogActions>
              </Dialog>
              {/* <FormControlLabel
                control={<Switch checked={pushNotificationsOn} onChange={() => setPushNotificationsOn(!pushNotificationsOn)} />}
                label={i18n.t('fireroverappnotifications')}
                style={widthStyle}
              /> */}
              <FormControlLabel
                control={<Switch checked={emailsOn} onChange={() => setEmailsOn(!emailsOn)} />}
                label={i18n.t('emailnotifications')}
                style={widthStyle}
              />
            </FormGroup>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Stack direction="row">
              <Typography variant="h6" paddingLeft={3} gutterBottom>{i18n.t('wheniwanttoreceivealerts')}</Typography>
              <IconButton onClick={() => setInfoModalVisible(true)} sx={{ padding: "0px 4px 0px 4px", marginBottom: "8px", marginLeft: "4px" }}>
                <InfoIcon />
              </IconButton>
            </Stack>

            <Stack padding={2}>
              <FormControl>
                <FormLabel id="weekdays-controlled-radio-buttons-group">{i18n.t('weekdays')}</FormLabel>
                <RadioGroup
                  aria-labelledby="weekdays-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={weekdayValue}
                  onChange={handleWeekdayChange}
                  >
                  <FormControlLabel value="on24/7" control={<Radio />} label={i18n.t('on247')} />
                  <FormControlLabel value="off" control={<Radio />} label={i18n.t('off')} />
                  <FormControlLabel value="accumulation" control={<Radio />} label={`${i18n.t('onperschedule')}: ${i18n.t('accumulation')} — ${i18n.t('accumulationinfo')}`} />
                  <FormControlLabel value="startStop" control={<Radio />} label={`${i18n.t('onperschedule')}: ${i18n.t('startstop')} — ${i18n.t('startstopinfo')}`} />
                </RadioGroup>
              </FormControl>
            </Stack>
            <Grid container direction="column" spacing={1}>
              {weekdaysOn && weekdayScheduleOn && (
                <>
                  <Grid item container spacing={2} alignItems="center" sx={{ paddingTop: "10px", paddingBottom: "5px" }}>
                    <Grid item>
                      <Typography>{i18n.t('starttime')}:</Typography>
                    </Grid>
                    <Grid item>
                      <TimePicker label={i18n.t('starttime')} value={weekdaysStartTime} onChange={setWeekDaysStartTime} />
                    </Grid>
                  </Grid>

                  <Grid item container spacing={2} alignItems="center">
                    <Grid item>
                      <Typography>{i18n.t('endtime')}:</Typography>
                    </Grid>
                    <Grid item style={{ paddingLeft: "21px", paddingBottom: "10px" }}>
                      <TimePicker label={i18n.t('endtime')} value={weekdaysEndTime} onChange={setWeekDaysEndTime} />
                    </Grid>
                  </Grid>
                </>
              )}

              <Stack paddingLeft={3}>  
                <FormControl>
                  <FormLabel id="weekends-controlled-radio-buttons-group">{i18n.t('weekends')}</FormLabel>
                  <RadioGroup
                    aria-labelledby="weekends-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={weekendValue}
                    onChange={handleWeekendChange}
                    >
                    <FormControlLabel value="on24/7" control={<Radio />} label={i18n.t('on247')} />
                    <FormControlLabel value="off" control={<Radio />} label={i18n.t('off')} />
                    <FormControlLabel value="accumulation" control={<Radio />} label={`${i18n.t('onperschedule')}: ${i18n.t('accumulation')} — ${i18n.t('accumulationinfo')}`} />
                    <FormControlLabel value="startStop" control={<Radio />} label={`${i18n.t('onperschedule')}: ${i18n.t('startstop')} — ${i18n.t('startstopinfo')}`} />
                  </RadioGroup>
                </FormControl>
              </Stack>
              {weekendsOn && weekendScheduleOn &&(
                <>
                  <Grid item container spacing={2} alignItems="center" sx={{ paddingTop: "10px", paddingBottom: "5px" }}>
                    <Grid item>
                      <Typography>{i18n.t('starttime')}:</Typography>
                    </Grid>
                    <Grid item>
                      <TimePicker label={i18n.t('starttime')} value={weekendsStartTime} onChange={setWeekEndsStartTime} />
                    </Grid>
                  </Grid>

                  <Grid item container spacing={2} alignItems="center">
                    <Grid item>
                      <Typography>{i18n.t('endtime')}:</Typography>
                    </Grid>
                    <Grid item style={{ paddingLeft: "21px", paddingBottom: "10px" }}>
                      <TimePicker label={i18n.t('endtime')} value={weekendsEndTime} onChange={setWeekEndsEndTime} />
                    </Grid>
                  </Grid>
                </>
              )}

              <Dialog open={infoModalVisible} onClose={() => setInfoModalVisible(false)}>
                <DialogTitle>{i18n.t('todcontrolinfo')}</DialogTitle>
                <DialogContent>
                  <Typography>
                    {i18n.t('todcontrolinfotip')}<br /><br />

                    <strong>{i18n.t('off')}</strong>: {i18n.t('offinfo')}<br /><br />
                    <strong>{i18n.t('on247')}</strong>: {i18n.t('on247info')}<br /><br />
                    <strong>{i18n.t('onperschedule')}: {i18n.t('accumulation')}</strong>: {i18n.t('accumulationinfo')}<br /><br />
                    <strong>{i18n.t('onperschedule')}: {i18n.t('startstop')}</strong>: {i18n.t('startstopinfo')}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setInfoModalVisible(false)}>{i18n.t('close')}</Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Typography variant="h6" paddingLeft={3} gutterBottom>
              {i18n.t('preferredlanguage')}{' '}
              <Typography component="span" variant="body1" sx={{ marginLeft: 2 }}>
                {i18n.t('preferredlanguageNote')}
              </Typography>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>{i18n.t('selectpreferredlanguage')}</InputLabel>
              <Select
                value={preferredLanguage}
                onChange={(e) => setPreferredLanguage(e.target.value)}
                label={i18n.t('selectpreferredlanguage')}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value}>{lang.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          <Grid item xs={12} style={{ paddingTop: "10px", paddingBottom: "10px" }}>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleSave}>
                {loading
                  ? <CircularProgress />
                  : i18n.t('save')}
              </Button>
            </Box>
          </Grid>
        </Container>
      </LocalizationProvider>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
        <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {i18n.t('notificationssettingssaved')}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
